import { useEffect, useRef, useState } from 'react';

/** No meaningful camera motion / input before "Are you still there?" */
const IDLE_BEFORE_PROMPT_MS = 3 * 60 * 1000;
/** Time to answer the prompt before marking Away */
const PROMPT_RESPONSE_MS = 60 * 1000;
/** Time in Away before auto-ending the stream */
const AWAY_BEFORE_END_MS = 10 * 60 * 1000;
const FRAME_SAMPLE_MS = 1000;
const MOTION_THRESHOLD = 12;
const SAMPLE_WIDTH = 64;
const SAMPLE_HEIGHT = 36;

export type StreamInactivityPhase = 'watching' | 'prompt' | 'away' | 'idle';

export interface UseStreamInactivityOptions {
  enabled: boolean;
  mediaStream: MediaStream | null;
  showStatus: string | null;
  onMarkAway: () => void;
  onAutoEnd: () => void;
}

export interface UseStreamInactivityResult {
  phase: StreamInactivityPhase;
  dismissPrompt: () => void;
}

function sampleFrameLuma(
  ctx: CanvasRenderingContext2D,
  video: HTMLVideoElement,
): Float32Array | null {
  if (video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
    return null;
  }
  ctx.drawImage(video, 0, 0, SAMPLE_WIDTH, SAMPLE_HEIGHT);
  const { data } = ctx.getImageData(0, 0, SAMPLE_WIDTH, SAMPLE_HEIGHT);
  const luma = new Float32Array(SAMPLE_WIDTH * SAMPLE_HEIGHT);
  for (let i = 0, p = 0; i < data.length; i += 4, p += 1) {
    const r = data[i] ?? 0;
    const g = data[i + 1] ?? 0;
    const b = data[i + 2] ?? 0;
    luma[p] = 0.299 * r + 0.587 * g + 0.114 * b;
  }
  return luma;
}

function meanAbsDiff(a: Float32Array, b: Float32Array): number {
  let sum = 0;
  const len = Math.min(a.length, b.length);
  for (let i = 0; i < len; i += 1) {
    sum += Math.abs(a[i]! - b[i]!);
  }
  return len === 0 ? 0 : sum / len;
}

/**
 * Netflix-style inactivity: frame motion + pointer/keyboard reset the idle clock.
 * Prompt → mark Away → extended Away → auto-end.
 */
export function useStreamInactivity(
  options: UseStreamInactivityOptions,
): UseStreamInactivityResult {
  const { enabled, mediaStream, showStatus, onMarkAway, onAutoEnd } = options;
  const [phase, setPhase] = useState<StreamInactivityPhase>('idle');
  const lastMotionAtRef = useRef(Date.now());
  const prevLumaRef = useRef<Float32Array | null>(null);
  const promptDeadlineRef = useRef<number | null>(null);
  const awayStartedAtRef = useRef<number | null>(null);
  const markedAwayRef = useRef(false);
  const endedRef = useRef(false);
  const onMarkAwayRef = useRef(onMarkAway);
  const onAutoEndRef = useRef(onAutoEnd);
  onMarkAwayRef.current = onMarkAway;
  onAutoEndRef.current = onAutoEnd;

  const dismissPrompt = (): void => {
    lastMotionAtRef.current = Date.now();
    promptDeadlineRef.current = null;
    markedAwayRef.current = false;
    setPhase(enabled && showStatus === 'LIVE' ? 'watching' : 'idle');
  };

  useEffect(() => {
    if (showStatus === 'AWAY') {
      if (awayStartedAtRef.current === null) {
        awayStartedAtRef.current = Date.now();
      }
      setPhase('away');
      return;
    }
    awayStartedAtRef.current = null;
    endedRef.current = false;
    markedAwayRef.current = false;
    promptDeadlineRef.current = null;

    if (!enabled || showStatus !== 'LIVE') {
      setPhase('idle');
      return;
    }

    setPhase('watching');
    lastMotionAtRef.current = Date.now();
  }, [enabled, showStatus]);

  useEffect(() => {
    if (!enabled || showStatus !== 'LIVE') {
      return;
    }

    const bump = (): void => {
      lastMotionAtRef.current = Date.now();
      if (promptDeadlineRef.current !== null) {
        promptDeadlineRef.current = null;
        setPhase('watching');
      }
    };

    window.addEventListener('pointerdown', bump);
    window.addEventListener('keydown', bump);
    return () => {
      window.removeEventListener('pointerdown', bump);
      window.removeEventListener('keydown', bump);
    };
  }, [enabled, showStatus]);

  useEffect(() => {
    if (!enabled || showStatus !== 'LIVE' || !mediaStream) {
      return;
    }

    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.srcObject = mediaStream;
    void video.play().catch(() => {
      /* autoplay may fail; frame sampling still retries */
    });

    const canvas = document.createElement('canvas');
    canvas.width = SAMPLE_WIDTH;
    canvas.height = SAMPLE_HEIGHT;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) {
      return;
    }

    const intervalId = window.setInterval(() => {
      const luma = sampleFrameLuma(ctx, video);
      if (!luma) {
        return;
      }
      const prev = prevLumaRef.current;
      prevLumaRef.current = luma;
      if (!prev) {
        return;
      }
      if (meanAbsDiff(prev, luma) >= MOTION_THRESHOLD) {
        lastMotionAtRef.current = Date.now();
        if (promptDeadlineRef.current !== null) {
          promptDeadlineRef.current = null;
          setPhase('watching');
        }
      }
    }, FRAME_SAMPLE_MS);

    return () => {
      window.clearInterval(intervalId);
      video.srcObject = null;
      prevLumaRef.current = null;
    };
  }, [enabled, mediaStream, showStatus]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const tickId = window.setInterval(() => {
      const now = Date.now();

      if (showStatus === 'AWAY') {
        const started = awayStartedAtRef.current ?? now;
        awayStartedAtRef.current = started;
        if (!endedRef.current && now - started >= AWAY_BEFORE_END_MS) {
          endedRef.current = true;
          onAutoEndRef.current();
        }
        return;
      }

      if (showStatus !== 'LIVE') {
        return;
      }

      if (promptDeadlineRef.current !== null) {
        if (now >= promptDeadlineRef.current && !markedAwayRef.current) {
          markedAwayRef.current = true;
          promptDeadlineRef.current = null;
          setPhase('away');
          onMarkAwayRef.current();
        }
        return;
      }

      if (now - lastMotionAtRef.current >= IDLE_BEFORE_PROMPT_MS) {
        promptDeadlineRef.current = now + PROMPT_RESPONSE_MS;
        setPhase('prompt');
      }
    }, 1000);

    return () => {
      window.clearInterval(tickId);
    };
  }, [enabled, showStatus]);

  return { phase, dismissPrompt };
}
